import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowPathIcon,
  ArrowRightIcon,
  CheckIcon,
  CommandLineIcon,
  LinkIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import { contextService } from '../../services/api';

const intents = [
  ['auto', 'Auto'],
  ['remember', 'Remember'],
  ['schedule', 'Schedule'],
  ['prepare', 'Prepare'],
  ['ask', 'Ask'],
];

const UniversalCaptureSheet = ({ open, onClose, defaultContextId = null }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [content, setContent] = useState('');
  const [intent, setIntent] = useState('auto');
  const [contextId, setContextId] = useState('');

  const { data } = useQuery({
    queryKey: ['contexts', 'capture'],
    queryFn: () => contextService.getContexts(),
    enabled: open,
  });
  const contexts = data?.contexts || [];

  useEffect(() => {
    if (open) setContextId(defaultContextId ? String(defaultContextId) : '');
  }, [defaultContextId, open]);

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    if (open) window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [open, onClose]);

  const mutation = useMutation({
    mutationFn: () => contextService.capture({
      content: content.trim(),
      intent,
      context_id: contextId ? Number(contextId) : null,
      source: 'manual',
      provenance: { channel: 'universal_capture' },
    }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['today'] });
      queryClient.invalidateQueries({ queryKey: ['inbox'] });
      queryClient.invalidateQueries({ queryKey: ['contexts'] });
      queryClient.invalidateQueries({ queryKey: ['notes'] });
      setContent('');
      setIntent('auto');
      setContextId('');
      onClose?.();

      if (result.route === '/ai') {
        navigate('/ai', { state: { prompt: result.prompt } });
        return;
      }
      if (result.context_id) {
        toast.success('Added to context');
        navigate(`/contexts/${result.context_id}`);
        return;
      }
      toast.success('Captured for review');
      navigate('/inbox');
    },
    onError: (error) => toast.error(error.response?.data?.detail || 'Could not capture this'),
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      <button
        className="absolute inset-0 h-full w-full bg-[#101813]/50 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close universal capture"
      />
      <section
        className="modal-safe absolute inset-x-0 bottom-0 mx-auto max-w-3xl rounded-t-lg border border-b-0 border-[#dce3dd] bg-white shadow-[0_-20px_60px_rgba(21,36,28,0.18)] sm:bottom-5 sm:rounded-lg sm:border"
        aria-labelledby="capture-title"
      >
        <div className="border-b border-[#e2e7e3] p-5 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[#dcece4] text-[#246b52]">
                <CommandLineIcon className="h-5 w-5" />
              </span>
              <div>
                <p className="text-[11px] font-bold uppercase text-[#246b52]">Ask or capture</p>
                <h2 id="capture-title" className="mt-1 text-lg font-bold">What should Timelyzer understand?</h2>
                <p className="mt-1 text-sm leading-5 text-[#6b766f]">
                  Write naturally. Timelyzer will route it without making you choose an app first.
                </p>
              </div>
            </div>
            <button onClick={onClose} className="icon-button shrink-0" aria-label="Close">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-5 sm:p-6">
          <textarea
            autoFocus
            value={content}
            onChange={(event) => setContent(event.target.value)}
            rows={5}
            className="field-input resize-none text-base leading-7"
            placeholder="Remember what Sarah said about pricing, prepare me for tomorrow's customer meeting, or move my workout to Friday..."
            aria-label="Universal capture"
          />

          <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_240px]">
            <div>
              <p className="field-label">Interpret as</p>
              <div className="flex flex-wrap gap-1 rounded-md border border-[#dce3dd] bg-[#f4f6f4] p-1">
                {intents.map(([value, label]) => (
                  <button
                    key={value}
                    onClick={() => setIntent(value)}
                    className={`min-h-8 rounded px-3 text-xs font-bold ${
                      intent === value ? 'bg-white text-[#1f6049] shadow-sm' : 'text-[#6c7770] hover:text-[#17211c]'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs leading-5 text-[#7a857e]">
                Auto is conservative: unclear captures go to Inbox instead of changing your schedule.
              </p>
            </div>

            <div>
              <label htmlFor="capture-context" className="field-label">Attach to context</label>
              <select
                id="capture-context"
                value={contextId}
                onChange={(event) => setContextId(event.target.value)}
                className="field-input"
              >
                <option value="">Let Timelyzer classify it</option>
                {contexts.map((context) => (
                  <option key={context.id} value={context.id}>{context.title}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-6 flex flex-col-reverse gap-3 border-t border-[#e7ebe8] pt-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 text-xs font-semibold text-[#6e7972]">
              {contextId ? <LinkIcon className="h-4 w-4 text-[#246b52]" /> : <SparklesIcon className="h-4 w-4 text-[#996617]" />}
              {contextId ? 'This becomes part of the selected context.' : 'Uncertain items stay visible in Inbox.'}
            </div>
            <button
              onClick={() => mutation.mutate()}
              disabled={!content.trim() || mutation.isPending}
              className="primary-button min-w-[150px] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {mutation.isPending ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  Understanding
                </>
              ) : (
                <>
                  <CheckIcon className="h-4 w-4" />
                  Continue
                  <ArrowRightIcon className="h-4 w-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UniversalCaptureSheet;
